import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { type SupportedLanguage } from './config';
import { normalizeLanguage } from './locales';
import { notifications } from '@mantine/notifications';
import { useAuth } from '@teletienda/shared-auth-ui';
import { coreAPI_Service } from '@/lib/api/core';

/**
 * Custom hook for language switching with backend sync
 */
export function useLanguageSwitcher() {
  const { i18n } = useTranslation();
  const { user, token } = useAuth();

  /**
   * Change language and sync with backend
   */
  const changeLanguage = useCallback(
    async (lang: SupportedLanguage) => {
      try {
        // Change i18next language
        await i18n.changeLanguage(lang);

        // Update document lang attribute for accessibility
        document.documentElement.lang = lang;

        // Sync with backend if user is authenticated
        if (user && token) {
          try {
            await coreAPI_Service.updateProfile({ language_code: lang });
            console.log(`[i18n] Language preference synced to backend: ${lang}`);
          } catch (backendError) {
            // Don't show error to user if backend sync fails
            // The language change in UI was successful, which is most important
            console.warn('[i18n] Failed to sync language to backend:', backendError);
          }
        } else {
          console.log(`[i18n] User not authenticated, skipping backend sync`);
        }

        notifications.show({
          title: i18n.t('common:language'),
          message: i18n.t('common:language_changed'),
          color: 'green',
        });
      } catch (error) {
        console.error('Failed to change language:', error);
        notifications.show({
          title: i18n.t('errors:generic_error'),
          message: i18n.t('errors:network_error'),
          color: 'red',
        });
      }
    },
    [i18n, user, token]
  );

  /**
   * Get current language
   */
  const currentLanguage = normalizeLanguage(i18n.language);

  return {
    currentLanguage,
    changeLanguage,
    isReady: i18n.isInitialized,
  };
}
